import { hasCrmAccess } from "@wa/core/crm-access";
import { api } from "./api";

export interface Membership {
  siteId: string;
  siteName: string;
  isHost: boolean;
  roleKey: string;
  roleName: Record<string, string>;
  isSuperadmin: boolean;
  permissions: string[];
}

export interface StaffUser {
  id: string;
  email: string;
  name: string;
  isSuperadmin: boolean;
  memberships: Membership[];
  createdAt?: string;
}

export async function getMe(): Promise<StaffUser | null> {
  try {
    return await api.get<StaffUser>("/me");
  } catch {
    return null;
  }
}

/** A login response: either the full user OR an MFA challenge (a second factor is needed). */
export interface MfaRequired {
  mfaRequired: true;
  pendingToken: string;
}
export type LoginResult = StaffUser | MfaRequired;

export function isMfaRequired(r: LoginResult): r is MfaRequired {
  return (r as MfaRequired).mfaRequired === true;
}

export async function login(email: string, password: string): Promise<LoginResult> {
  return api.post<LoginResult>("/auth/login", { email, password });
}

export async function logout(): Promise<void> {
  await api.post("/auth/logout");
}

/**
 * The staff CRM is a PLATFORM tool: only members of the host site get in
 * (a superadmin OR an `isHost` membership). An ordinary client cannot.
 *
 * The rule itself lives in `@wa/core/crm-access`, because the admin center and
 * the account hub draw a link into the CRM and must ask the same question. A
 * second copy here would drift, and the drift would be silent: a menu entry
 * leading to an access-denied screen, or shown to a portal client.
 */
export function isPlatformStaff(user: StaffUser): boolean {
  return hasCrmAccess(user);
}

/** Platform-scope permission check (a superadmin gets everything; otherwise the host membership decides). */
export function userCanPlatform(user: StaffUser, perm: string): boolean {
  if (user.isSuperadmin) return true;
  const host = user.memberships.find((m) => m.isHost);
  if (!host) return false;
  return host.permissions.includes("*") || host.permissions.includes(perm);
}

/**
 * Any-of permission check.
 *
 * A section whose page is a set of independently gated tabs cannot be guarded
 * by one key. Settings is that shape: invoicing, appearance, labels,
 * integrations and roles each already answer to their own right, and picking
 * any single one of them as the door key either locks out a tab's rightful
 * owner or hands everybody the whole page. The list is the union of the tab
 * gates (see `sections/settings/perms.ts`) and only decides whether the door is
 * worth showing - the tabs behind it stay the real gate, and so does the API.
 *
 * An empty list is false: a section with no reachable tab has no door.
 */
export function userCanPlatformAny(user: StaffUser, perm: string | readonly string[]): boolean {
  if (typeof perm === "string") return userCanPlatform(user, perm);
  return perm.some((p) => userCanPlatform(user, p));
}

/** The host site id (for the `X-Site-Id` on media requests; the host site is the platform or agency). */
export function hostSiteId(user: StaffUser): string | null {
  return user.memberships.find((m) => m.isHost)?.siteId ?? user.memberships[0]?.siteId ?? null;
}

/**
 * Sibling SPA addresses (admin center, account hub) live in `lib/urls.ts`.
 * Re-exported here because every caller already imports its user/session
 * helpers from this module.
 */
export { ACCOUNT_URL, ADMIN_URL } from "./urls";
